import {useCallback, useMemo, useState} from 'react';

export const StudioTab = {
	Prompt: 'prompt',
	AgentMd: 'agentMd',
	Deploy: 'deploy',
} as const;
export type StudioTab = (typeof StudioTab)[keyof typeof StudioTab];

// Which list the left panel shows. Workflows is the default, so reopening the Studio lands there.
export const LibraryTab = {
	AgenticWorkflows: 'agenticWorkflows',
	TaskAgents: 'taskAgents',
} as const;
export type LibraryTab = (typeof LibraryTab)[keyof typeof LibraryTab];

export interface StudioAgentEntry {
	agentId: string;
	name: string;
	categoryId: string;
	title: string;
	description: string;
	// Set for an agentic workflow's record. A workflow has no category or description and is shown by
	// its declared title rather than its name.
	isWorkflow?: boolean;
}

// An agentic workflow as it declares itself: listed by title, with the record it is bound to and the
// page it appears on. Its prompts, model and files live on the Nova agent record with the same agentId.
export interface StudioWorkflowEntry {
	agentId: string;
	title: string;
	boundRecord: string;
	page: string;
}

export function workflowToAgentEntry(workflow: StudioWorkflowEntry): StudioAgentEntry {
	return {
		agentId: workflow.agentId,
		// The service stores a workflow's record under its agentId as the name.
		name: workflow.agentId,
		categoryId: '',
		title: workflow.title,
		description: '',
		isWorkflow: true,
	};
}

// The name the header and the other tabs show: a workflow's declared title, never its agentId.
export function studioAgentDisplayName(agent: StudioAgentEntry): string {
	return agent.isWorkflow ? agent.title : agent.name;
}

export interface StudioCategoryEntry {
	categoryId: string;
	name: string;
	isUncategorised: boolean;
}

export interface CatalogueGroup {
	category: StudioCategoryEntry;
	agents: StudioAgentEntry[];
}

export interface StudioCatalogue {
	groups: CatalogueGroup[];
}

export function buildStudioCatalogue(categories: StudioCategoryEntry[], agents: StudioAgentEntry[]): StudioCatalogue {
	const knownCategoryIds = new Set(categories.map(category => category.categoryId));
	// Uncategorised is pinned to the top of the panel wherever the catalogue happened to return it, so
	// the group holding anything an author has not filed yet is the first thing they see rather than
	// something they scroll past a list of real categories to find. Every other category keeps the
	// order it arrived in — this sorts on one boolean only, and `sort` is stable, so nothing else moves.
	const orderedCategories = [...categories].sort(
		(first, second) => Number(second.isUncategorised) - Number(first.isUncategorised)
	);
	const groups = orderedCategories.map(category => ({
		category,
		agents: agents.filter(
			agent =>
				agent.categoryId === category.categoryId ||
				(category.isUncategorised && !knownCategoryIds.has(agent.categoryId))
		),
	}));
	return {groups};
}

export interface UseNovaStudioShellOptions {
	categories: StudioCategoryEntry[];
	agents: StudioAgentEntry[];
	workflows?: StudioWorkflowEntry[];
	initialSelectedAgentId?: string | null;
	initialTab?: StudioTab;
}

export interface NovaStudioShellState {
	catalogue: StudioCatalogue;
	selectedAgentId: string | null;
	selectedAgent: StudioAgentEntry | null;
	activeTab: StudioTab;
	libraryTab: LibraryTab;
	selectAgent: (agentId: string | null) => void;
	setActiveTab: (tab: StudioTab) => void;
	setLibraryTab: (tab: LibraryTab) => void;
}

const NO_WORKFLOWS: StudioWorkflowEntry[] = [];

export function useNovaStudioShell(options: UseNovaStudioShellOptions): NovaStudioShellState {
	const {categories, agents, workflows = NO_WORKFLOWS} = options;
	const [selectedAgentId, setSelectedAgentId] = useState<string | null>(options.initialSelectedAgentId ?? null);
	const [activeTab, setActiveTab] = useState<StudioTab>(options.initialTab ?? StudioTab.Prompt);
	// Held for as long as the Studio stays mounted; a fresh mount starts on Workflows again.
	const [libraryTab, setLibraryTab] = useState<LibraryTab>(LibraryTab.AgenticWorkflows);

	const catalogue = useMemo(() => buildStudioCatalogue(categories, agents), [categories, agents]);
	const selectedAgent = useMemo(() => {
		const agent = agents.find(candidate => candidate.agentId === selectedAgentId);
		if (agent) {
			return agent;
		}
		const workflow = workflows.find(candidate => candidate.agentId === selectedAgentId);
		return workflow ? workflowToAgentEntry(workflow) : null;
	}, [agents, workflows, selectedAgentId]);

	const selectAgent = useCallback(
		(agentId: string | null) => {
			// Opening a different agent lands on its Prompt tab; switching tabs within
			// the same agent stays put (tabs are worked independently).
			if (agentId !== selectedAgentId) {
				setActiveTab(StudioTab.Prompt);
			}
			setSelectedAgentId(agentId);
		},
		[selectedAgentId]
	);

	return {
		catalogue,
		selectedAgentId,
		selectedAgent,
		activeTab,
		libraryTab,
		selectAgent,
		setActiveTab,
		setLibraryTab,
	};
}
